// Facturación (CA-O4, ADR-0047): ADMINISTRATIVE records of the invoices Media Interactiva issues from
// Honduras and of the payments received. Never a fiscal invoice: no electronic invoicing, no tax computation
// (Honduran requirements REQUIRES VERIFICATION). Test and real are always shown apart; real records stay
// disabled until the owner's policy decision. Balances, states and "billed twice" are decided by the
// database; the browser only collects input and renders what the server returns.
import { useQuery } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";
import { toast } from "sonner";
import { api, call } from "@/api/client";
import { ApiProblem } from "@/api/errors";
import { Badge, type BadgeTone } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Field } from "@/ui/field";
import { Input } from "@/ui/input";
import { Callout, EmptyState, PageHeader, Section, Table, Td, Th } from "@/ui/layout";
import { PageError, PageLoading, useTitle } from "../common/page";
import { formField, ruleMessage, usd } from "../commercial/labels";
import { adminKeys, adminPost, useAdmin, useAdminMutation } from "./admin-api";
import { useOperatorRoles } from "./admin-layout";

interface KindSummary {
  invoices: number;
  outstandingMicros: number;
  overdueMicros: number;
  overdueCount: number;
}
interface Summary {
  real: KindSummary;
  test: KindSummary;
  realBillingEnabled: boolean;
}
interface InvoiceRow {
  id: string;
  organizationId: string;
  company: string;
  invoiceNumber: string;
  issuedOn: string;
  dueOn: string;
  periodStart: string;
  periodEnd: string;
  amountMicros: number;
  balanceMicros: number;
  state: "ISSUED" | "PARTIALLY_PAID" | "PAID" | "OVERDUE" | "VOID";
  isTest: boolean;
}
interface InvoiceDetail {
  invoice: { void_reason: string | null; notes: string | null };
  lines: Array<{ id: string; campaign: string; description: string; impressions: number | null; amountMicros: number }>;
  payments: Array<{
    id: string;
    amount_micros: number;
    received_on: string;
    method: string;
    reference: string;
    status: string;
    void_reason: string | null;
  }>;
}
interface Billable {
  authorizationId: string;
  campaign: string;
  consumedMicros: number;
  invoicedMicros: number;
  lastInvoicedUntil: string | null;
}

export const INVOICE_STATE: Record<InvoiceRow["state"], { label: string; tone: BadgeTone }> = {
  ISSUED: { label: "Emitida", tone: "info" },
  PARTIALLY_PAID: { label: "Pago parcial", tone: "warning" },
  PAID: { label: "Pagada", tone: "success" },
  OVERDUE: { label: "Vencida", tone: "danger" },
  VOID: { label: "Anulada", tone: "neutral" },
};
const METHODS: Record<string, string> = {
  TRANSFER: "Transferencia",
  CHECK: "Cheque",
  CASH: "Efectivo",
  CARD: "Tarjeta",
  OTHER: "Otro",
};
const select =
  "h-8 w-full rounded-md border border-line-strong bg-surface px-2 text-sm text-fg focus-visible:outline-2 focus-visible:outline-brand";
const problemText = (e: unknown) => (e instanceof ApiProblem ? ruleMessage(e) : "No se pudo completar la acción.");
const today = () => new Date().toISOString().slice(0, 10);
const usdText = (m: number) => (m / 1_000_000).toFixed(2);

export function AdminBilling() {
  useTitle("Facturación");
  const roles = useOperatorRoles();
  const canManage = roles.includes("SUPER_ADMIN") || roles.includes("FINANCE_ADMIN");
  const summary = useAdmin<Summary>("/admin/billing/summary");
  const [state, setState] = useState("");
  const invoices = useAdmin<{ items: InvoiceRow[] }>("/admin/invoices", { state: state || undefined });
  if (summary.isLoading) return <PageLoading />;
  if (summary.error || !summary.data) return <PageError error={summary.error} />;
  const s = summary.data;
  return (
    <div className="grid gap-8">
      <PageHeader
        title="Facturación"
        description="Registro administrativo de las facturas que Media Interactiva emite desde Honduras y de los pagos recibidos. No es facturación electrónica ni calcula impuestos."
      />
      {!s.realBillingEnabled ? (
        <Callout title="Facturación real deshabilitada">
          Solo se registran facturas y pagos de empresas marcadas como datos de prueba. Los requisitos fiscales de
          Honduras deben definirse antes de activar la facturación real (CA-O4).
        </Callout>
      ) : null}
      <Section title="Saldos">
        <div className="grid gap-3 sm:grid-cols-2">
          {(
            [
              ["Operación real", s.real],
              ["Datos de prueba", s.test],
            ] as const
          ).map(([label, k]) => (
            <div key={label} className="rounded-md border border-line p-4 text-sm">
              <div className="font-medium">{label}</div>
              <div className="mt-2 grid gap-1 text-muted">
                <span>
                  {String(k.invoices)} facturas · pendiente {usd(k.outstandingMicros)}
                </span>
                <span className={k.overdueCount > 0 ? "text-danger" : undefined}>
                  {String(k.overdueCount)} vencidas · {usd(k.overdueMicros)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </Section>
      {canManage ? <NewInvoice /> : null}
      <Section
        title="Facturas"
        actions={
          <select
            aria-label="Filtrar por estado"
            className={select}
            value={state}
            onChange={(e) => {
              setState(e.target.value);
            }}
          >
            <option value="">Todas</option>
            {Object.entries(INVOICE_STATE).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        }
      >
        {!invoices.data || invoices.data.items.length === 0 ? (
          <EmptyState title="Sin facturas registradas" />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>Factura</Th>
                  <Th>Empresa</Th>
                  <Th>Período</Th>
                  <Th>Vence</Th>
                  <Th className="text-right">Importe</Th>
                  <Th className="text-right">Saldo</Th>
                  <Th>Estado</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {invoices.data.items.map((i) => (
                  <InvoiceRowView key={i.id} invoice={i} canManage={canManage} />
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Section>
    </div>
  );
}

function InvoiceRowView({ invoice: i, canManage }: { invoice: InvoiceRow; canManage: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr>
        <Td className="font-mono text-xs">
          {i.invoiceNumber}
          {i.isTest ? <Badge className="ml-2">Prueba</Badge> : null}
        </Td>
        <Td>{i.company}</Td>
        <Td className="whitespace-nowrap text-xs">
          {i.periodStart} → {i.periodEnd}
        </Td>
        <Td className="whitespace-nowrap text-xs">{i.dueOn}</Td>
        <Td className="text-right tabular-nums">{usd(i.amountMicros)}</Td>
        <Td className="text-right tabular-nums">{usd(i.balanceMicros)}</Td>
        <Td>
          <Badge tone={INVOICE_STATE[i.state].tone}>{INVOICE_STATE[i.state].label}</Badge>
        </Td>
        <Td>
          <Button
            size="sm"
            variant="ghost"
            aria-expanded={open}
            onClick={() => {
              setOpen(!open);
            }}
          >
            {open ? "Cerrar" : "Ver"}
          </Button>
        </Td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={8} className="bg-sunken p-4">
            <InvoiceDetailView invoice={i} canManage={canManage} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function InvoiceDetailView({ invoice: i, canManage }: { invoice: InvoiceRow; canManage: boolean }) {
  const detail = useQuery({
    queryKey: adminKeys.path("/admin/invoices/detail", { id: i.id }),
    queryFn: async () => {
      const r = await call(() =>
        api.GET("/admin/invoices/{invoiceId}/detail", { params: { path: { invoiceId: i.id } } })
      );
      return r.data as unknown as InvoiceDetail;
    },
  });
  const pay = useAdminMutation((body: Parameters<typeof adminPost.payment>[1]) => adminPost.payment(i.id, body));
  const voidInvoice = useAdminMutation((reason: string) => adminPost.voidInvoice(i.id, reason));
  const voidPayment = useAdminMutation((input: { id: string; reason: string }) =>
    adminPost.voidPayment(input.id, input.reason)
  );
  if (!detail.data) return <p className="text-sm text-muted">Cargando…</p>;
  const d = detail.data;
  return (
    <div className="grid gap-4 text-sm">
      <ul className="grid gap-1">
        {d.lines.map((l) => (
          <li key={l.id}>
            {l.campaign} · {l.description}
            {l.impressions !== null ? ` · ${l.impressions.toLocaleString("es-HN")} impresiones` : ""} ·{" "}
            <span className="tabular-nums">{usd(l.amountMicros)}</span>
          </li>
        ))}
      </ul>
      {d.invoice.void_reason ? <p className="text-muted">Anulada: {d.invoice.void_reason}</p> : null}
      <div className="grid gap-1">
        <h4 className="font-medium">Pagos</h4>
        {d.payments.length === 0 ? <p className="text-muted">Sin pagos.</p> : null}
        {d.payments.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-2">
            <span className={p.status === "VOID" ? "text-muted line-through" : undefined}>
              {p.received_on} · {METHODS[p.method] ?? p.method} · {p.reference} · {usd(p.amount_micros)}
            </span>
            {p.status === "VOID" ? <span className="text-xs text-muted">Anulado: {p.void_reason}</span> : null}
            {canManage && p.status === "RECORDED" ? (
              <ReasonAction
                label="Anular pago"
                pending={voidPayment.isPending}
                onSubmit={(reason, done) => {
                  voidPayment.mutate(
                    { id: p.id, reason },
                    {
                      onSuccess: () => {
                        toast.success("Pago anulado: el saldo volvió a quedar pendiente.");
                        void detail.refetch();
                        done();
                      },
                      onError: (e) => toast.error(problemText(e)),
                    }
                  );
                }}
              />
            ) : null}
          </div>
        ))}
      </div>
      {canManage && i.state !== "VOID" && i.balanceMicros > 0 ? (
        <form
          className="grid gap-2 sm:grid-cols-5 sm:items-end"
          onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
            event.preventDefault();
            const f = new FormData(event.currentTarget);
            const form = event.currentTarget;
            pay.mutate(
              {
                amountUsd: formField(f, "amountUsd"),
                receivedOn: formField(f, "receivedOn"),
                method: formField(f, "method") as "TRANSFER",
                reference: formField(f, "reference"),
                idempotencyKey: crypto.randomUUID(),
              },
              {
                onSuccess: () => {
                  toast.success("Pago registrado.");
                  form.reset();
                  void detail.refetch();
                },
                onError: (e) => toast.error(problemText(e)),
              }
            );
          }}
        >
          <Field label="Importe (USD)">
            <Input name="amountUsd" inputMode="decimal" defaultValue={usdText(i.balanceMicros)} required />
          </Field>
          <Field label="Recibido el">
            <Input name="receivedOn" type="date" max={today()} defaultValue={today()} required />
          </Field>
          <Field label="Método">
            <select name="method" className={select} defaultValue="TRANSFER">
              {Object.entries(METHODS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Referencia">
            <Input name="reference" minLength={2} maxLength={80} required />
          </Field>
          <Button type="submit" variant="primary" disabled={pay.isPending}>
            Registrar pago
          </Button>
        </form>
      ) : null}
      {canManage && i.state !== "VOID" ? (
        <ReasonAction
          label="Anular factura"
          pending={voidInvoice.isPending}
          onSubmit={(reason, done) => {
            voidInvoice.mutate(reason, {
              onSuccess: () => {
                toast.success("Factura anulada.");
                done();
              },
              onError: (e) => toast.error(problemText(e)),
            });
          }}
        />
      ) : null}
    </div>
  );
}

/** A destructive-looking action that asks for its reason inline (audited by the server). */
function ReasonAction({
  label,
  pending,
  onSubmit,
}: {
  label: string;
  pending: boolean;
  onSubmit: (reason: string, done: () => void) => void;
}) {
  const [open, setOpen] = useState(false);
  if (!open)
    return (
      <div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setOpen(true);
          }}
        >
          {label}
        </Button>
      </div>
    );
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSubmit(formField(new FormData(event.currentTarget), "reason").trim(), () => {
          setOpen(false);
        });
      }}
    >
      <Field label="Motivo">
        <Input name="reason" minLength={3} maxLength={500} required />
      </Field>
      <Button type="submit" size="sm" variant="secondary" disabled={pending}>
        Confirmar
      </Button>
    </form>
  );
}

/** Registers an invoice from the measured consumption not invoiced yet (lines per campaign authorization). */
function NewInvoice() {
  const companies = useAdmin<{
    positions: Array<{ organizationId: string; company: string; isTest: boolean }>;
  }>("/admin/credit-control");
  const [orgId, setOrgId] = useState("");
  const billable = useQuery({
    enabled: orgId !== "",
    queryKey: adminKeys.path("/admin/companies/billable", { orgId }),
    queryFn: async () => {
      const r = await call(() => api.GET("/admin/companies/{orgId}/billable", { params: { path: { orgId } } }));
      return (r.data as unknown as { items: Billable[] }).items;
    },
  });
  const issue = useAdminMutation((body: Parameters<typeof adminPost.invoice>[1]) => adminPost.invoice(orgId, body));
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    const lines = (billable.data ?? [])
      .filter((b) => f.get(`include-${b.authorizationId}`) === "on")
      .map((b) => ({
        authorizationId: b.authorizationId,
        description: `Consumo CPM · ${b.campaign}`,
        impressions: null,
        amountUsd: formField(f, `amount-${b.authorizationId}`),
      }));
    if (lines.length === 0) {
      toast.error("Elegí al menos una campaña.");
      return;
    }
    issue.mutate(
      {
        invoiceNumber: formField(f, "invoiceNumber"),
        issuedOn: formField(f, "issuedOn"),
        dueOn: formField(f, "dueOn"),
        periodStart: formField(f, "periodStart"),
        periodEnd: formField(f, "periodEnd"),
        notes: formField(f, "notes") || null,
        idempotencyKey: crypto.randomUUID(),
        lines,
      },
      {
        onSuccess: () => {
          toast.success("Factura registrada.");
          setOrgId("");
        },
        onError: (e) => toast.error(problemText(e)),
      }
    );
  };
  return (
    <Section
      title="Registrar factura"
      description="Una línea por campaña. Una campaña nunca se factura dos veces para períodos que se superponen."
    >
      <form className="grid max-w-3xl gap-3 sm:grid-cols-2" onSubmit={submit}>
        <Field label="Empresa" className="sm:col-span-2">
          <select
            className={select}
            value={orgId}
            onChange={(e) => {
              setOrgId(e.target.value);
            }}
            required
          >
            <option value="">Elegí una empresa con condiciones comerciales</option>
            {(companies.data?.positions ?? []).map((p) => (
              <option key={p.organizationId} value={p.organizationId}>
                {p.company}
                {p.isTest ? " (prueba)" : ""}
              </option>
            ))}
          </select>
        </Field>
        {orgId && billable.data ? (
          billable.data.length === 0 ? (
            <p className="text-sm text-muted sm:col-span-2">Esta empresa no tiene campañas aprobadas.</p>
          ) : (
            <fieldset className="grid gap-2 sm:col-span-2">
              <legend className="mb-1 text-xs font-medium">Campañas</legend>
              {billable.data.map((b) => (
                <div key={b.authorizationId} className="grid items-end gap-2 sm:grid-cols-[1fr_10rem]">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" name={`include-${b.authorizationId}`} defaultChecked />
                    <span>
                      {b.campaign}
                      <span className="block text-xs text-muted">
                        Consumido {usd(b.consumedMicros)} · facturado {usd(b.invoicedMicros)}
                        {b.lastInvoicedUntil ? ` (hasta ${b.lastInvoicedUntil})` : ""}
                      </span>
                    </span>
                  </label>
                  <Field label="Importe (USD)">
                    <Input
                      name={`amount-${b.authorizationId}`}
                      inputMode="decimal"
                      defaultValue={usdText(Math.max(b.consumedMicros - b.invoicedMicros, 0))}
                      required
                    />
                  </Field>
                </div>
              ))}
            </fieldset>
          )
        ) : null}
        <Field label="Número de factura">
          <Input name="invoiceNumber" maxLength={60} required />
        </Field>
        <Field label="Fecha de emisión">
          <Input name="issuedOn" type="date" defaultValue={today()} required />
        </Field>
        <Field label="Período desde">
          <Input name="periodStart" type="date" required />
        </Field>
        <Field label="Período hasta">
          <Input name="periodEnd" type="date" required />
        </Field>
        <Field label="Vence el">
          <Input name="dueOn" type="date" required />
        </Field>
        <Field label="Notas internas" hint="Opcional.">
          <Input name="notes" maxLength={1000} />
        </Field>
        <div className="sm:col-span-2">
          <Button type="submit" variant="primary" disabled={issue.isPending || !orgId}>
            Registrar factura
          </Button>
        </div>
      </form>
    </Section>
  );
}
